import React, { useEffect, useRef, useState } from 'react';
import { Container } from '../ui/Container';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { getImagePath } from '../../Utils/Utilities';

const TeamSection = () => {
  const isMobile = useIsMobile();
  const teamRef = useRef(null);
  const [isTeamVisible, setIsTeamVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTeamVisible(true);
        }
      },
      { threshold: 0.1 } // Triggers when 10% of the section is visible
    );

    if (teamRef.current) {
      observer.observe(teamRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const founders = [
    {
      name: 'Ekta Shah',
      title: 'Co-Founder & CEO',
      image: getImagePath('/Images/ektashah.jpg'),
      description: 'Ekta pursued a career in Computer Architecture at Qualcomm Inc. (San Diego) after studying at the University of Illinois at Urbana-Champaign (USA). Along with empowering Indian MSMEs with ERP, she also plays an active role in India\'s Startup ecosystem.',
      linkedIn: 'https://in.linkedin.com/in/ekta1651'
    },
    {
      name: 'Parth Shah',
      title: 'Co-Founder & Chief Evangelist',
      image: getImagePath('/Images/parthshah2.jpg'),
      description: 'Parth studied at the University of Texas at Austin (USA) before joining the Peach group to participate in our nation\'s growth story. While he has 15+ years experience in ERP software, he has also innovated solutions in Embedded Systems and e-Governance.',
      linkedIn: 'https://in.linkedin.com/in/parthshah123'
    }
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = [
    getImagePath('/Images/BiziPeach1.png'),
    getImagePath('/Images/BiziModi1.png'),
    getImagePath('/Images/BiziPeacock1.png'),
    getImagePath('/Images/BiziTeam1.png')
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  // Mobile Layout
  if (isMobile) {
    return (
      <section id="bzvzTeam" className="bg-background-light pb-12 md:py-16 lg:py-20 border-top">

        {/* Slideshow - OUTSIDE Container for full width */}
        <div className="w-full pb-5">
          <div className="relative w-full h-auto">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Team ${index + 1}`}
                className={`w-full h-auto transition-opacity duration-500 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute top-0 left-0'}`}
              />
            ))}
          </div>
        </div>

        <Container>
          <div className="text-center">
            {/* Main Header */}
            <h2 className="text-[30px] md:text-[40px] font-bold secttl mb-0">
              Team Biziverse
            </h2>

            {/* Description */}
            <div className="text-text-primary px-4 md:px-8 mt-5 leading-relaxed space-y-4 md:space-y-3 max-w-3xl mx-auto">
              <p className="text-[14px] md:text-lg text-text-primary">
                Biziverse Pvt. Ltd. is a govt-recognized startup based in Gandhinagar, Gujarat.
              </p>

              <p className="text-[14px] md:text-lg text-text-primary">
                It is the latest venture of the Peach Group, established in <b>1985</b> by US-returned entrepreneurs <b>Haren Shah</b> and <b>Mayoor Amin</b>.
              </p>

              <p className="text-[14px] md:text-lg text-text-primary">
                Powered by their experience and the energy of their next generation, Ekta & Parth, Biziverse aims to "make every small business a <span className="whitespace-nowrap">smart business"</span>.
              </p>
            </div>


            {/* Founders Section */}
            <div id="bzvzFounders" className="mt-12 mb-8">
              <h3 className="text-[30px] md:text-[40px] secttl font-bold text-primary">
                Our Founders
              </h3>
            </div>

            {/* Founder Cards */}
            <div className="mt-8 space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-8 max-w-md md:max-w-5xl mx-auto px-4">
              {founders.map((founder, index) => (
                <div
                  key={index}
                  className="bg-background-gray rounded p-4 text-center"
                >
                  {/* Image */}
                  <div className="pt-5 pb-2.5">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-[60%] max-w-[200px] mx-auto rounded"
                    />
                  </div>

                  {/* Name & Title */}
                  <div className="py-2.5 px-2 leading-tight">
                    <div className="font-semibold text-base md:text-lg text-text-primary">
                      {founder.name}
                      <svg
                        className="inline-block w-4 h-4 ml-1 text-[#0077B5]"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </div>
                    <div className="text-[14px] md:text-lg text-text-primary font-semibold">
                      {founder.title}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="text-[14px] md:text-lg text-text-primary leading-relaxed ">
                    {founder.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
    );
  }

  // Desktop Layout
  return (
    <section id="bzvzTeam" className="bg-white border-top">
      {/* First Section - Video and Customer Count */}
      <div className="hidden border-gray-300 py-14 px-[6%]">
        <Container variant="wide">
          <div className="flex items-center">
            {/* Video Section - Left Side */}
            <div className="w-[58.33%] pr-8">
              <video
                className="w-[80%] rounded-[5px]"
                loop
                autoPlay
                muted
                playsInline
              >
                <source src={getImagePath("/videos/18000CustomersV.mp4")} type="video/mp4" />
              </video>
            </div>

            {/* Text Section - Right Side */}
            <div className="w-[41.67%] flex flex-col justify-center" style={{ height: '47vh' }}>
              <h1 className="m-0 text-[40px] text-center text-[#333] font-bold" style={{ textShadow: '2px 2px 3px rgba(0, 0, 0, 0.1)' }}>
                Serving <span className="text-primary font-bold">40,000+ </span>
                <span className="font-semibold block text-[25px] my-5 leading-[35px]" style={{ textShadow: 'none', color: 'gray' }}>
                  Manufacturers, Traders, Retailers,<br />Service Providers
                </span>
              </h1>
            </div>
          </div>
        </Container>
      </div>

      {/* Second Section - Mission and Team */}
      <div className="bg-white py-12 md:py-16 lg:py-20 px-[6%]">
        <Container variant="wide">
          <div className="text-center mb-12">
            <h2 className="text-[30px] md:text-[40px] font-bold text-primary mb-3">
              Our Team
            </h2>
          </div>
          <div className="flex gap-2">
            {/* Mission Section - Left Side */}
            <div className="w-[41.67%]">
              <div className="text-center">
                <img src={getImagePath("/videos/Indiagif2.gif")} className="w-[185px] mx-auto" alt="India" />
                <div className="block text-center font-bold text-xl md:text-2xl leading-[35px] mb-0" style={{ color: 'gray' }}>
                  <span>Our Mission</span>
                </div>
                <div className="text-[#333] leading-[45px] mb-5 mt-0 text-[33px] font-bold" >
                  <span className="text-primary inline-block">Smart</span> Business
                  <br />
                  <span className="text-primary inline-block">Strong</span> India
                </div>
                <div className="px-[50px] text-text-primary font-semibold leading-[30px]">
                  We are a startup with a mission to help every Indian MSME become world-class using easy & low-cost software tools. Our talented team, US-returned founders, and globally-spread investors combine their energy and experience to give you the very best tech tools.
                </div>
              </div>
            </div>

            {/* Team Section - Right Side */}
            <div
              ref={teamRef}
              className={`w-[58.33%] transition-all duration-1000 ease-out transform ${isTeamVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                }`}
            >
              <div className="px-0">
                <img src={getImagePath("/images/1944Team2.jpeg")} className="w-full rounded-[5px]" alt="Team" />
                <div className="pt-[30px] px-10 pb-0 text-text-primary font-semibold leading-[30px] text-center">
                  Biziverse is powered by dynamism of youth and maturity of experience. <br />
                  We are fortunate to work with talented technocrats, smart managers, and caring support executives, collectively strengthening our principles of <b>"Innovation, Integrity, Impact"</b>.
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </section>
  );
};

export default TeamSection;